import { useMutation } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { type CustomerStrategyCustomerAttributeFilterItemInput } from '~/gql/graphql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

import { type InputValues } from '../utils'

import { useStrategyMemoId } from './useStrategyMemoId'

const document = graphql(`
  mutation StrategyMemoEditPage_UpdateStrategyMemo(
    $strategyMemoId: ID!
    $title: String
    $goal: String
    $surveyId: ID
    $brandGroupId: Int
    $brandId: Int
    $dataDisplayType: DataDisplayType
    $countOfAnswers: Float
    $filter: [[CustomerStrategyCustomerAttributeFilterItemInput!]!]!
  ) {
    updateCustomerStrategyMemo(
      id: $strategyMemoId
      title: $title
      goal: $goal
      surveyId: $surveyId
      brandGroupId: $brandGroupId
      brandId: $brandId
      dataDisplayType: $dataDisplayType
      countOfAnswers: $countOfAnswers
      customFilter: { filter: $filter }
    ) {
      id
      strategies {
        id
        strategyType
        showArrow
        segmentRoutes {
          toSegmentNumber
          fromSegmentNumber
        }
        who
        what
        how
      }
    }
  }
`)

const createDocument = graphql(`
  mutation StrategyMemoEditPage_CreateStrategy($strategyMemoId: ID!) {
    createCustomerStrategy(customerStrategyMemoId: $strategyMemoId) {
      id
    }
  }
`)

const updateDocument = graphql(`
  mutation StrategyMemoEditPage_UpdateStrategy(
    $strategyId: ID!
    $how: String
    $segmentRoutes: [CustomerStrategyRouteInput!]
    $showArrow: Boolean
    $strategyType: CustomerStrategyType
    $what: String
    $who: String
  ) {
    updateCustomerStrategy(
      id: $strategyId
      how: $how
      segmentRoutes: $segmentRoutes
      showArrow: $showArrow
      strategyType: $strategyType
      what: $what
      who: $who
    ) {
      id
    }
  }
`)

const deleteDocument = graphql(`
  mutation StrategyMemoEditPage_DeleteStrategy($strategyId: ID!) {
    deleteCustomerStrategy(id: $strategyId) {
      result
    }
  }
`)

export const useUpdateStrategyMemoMutation = (
  options: MutationOptions<void, void> = {},
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const strategyMemoId = useStrategyMemoId()
  const { getValues, setValue } = useFormContext<InputValues>()

  return useMutation({
    mutationFn: async () => {
      const values = getValues()

      const result = await graphQLRequest({
        document,
        variables: {
          strategyMemoId,
          ...R.pick(values, [
            'title',
            'goal',
            'surveyId',
            'brandGroupId',
            'brandId',
            'dataDisplayType',
          ]),
          countOfAnswers: values.surveyFilter?.count ?? null,
          filter: (values.surveyFilter?.filterGroups ??
            []) as CustomerStrategyCustomerAttributeFilterItemInput[][],
        },
      })

      const previousStrategies = result.updateCustomerStrategyMemo?.strategies
      const ok = exists(previousStrategies)
      if (!ok) {
        throw new Error(t('update_failed_4'))
      }

      const nextStrategies = values.strategies
      const deletingStrategies = R.differenceWith(
        previousStrategies,
        nextStrategies,
        (a, b) => a.id === b.id,
      )

      await Promise.all(
        deletingStrategies.map(async (strategy) => {
          const result = await graphQLRequest({
            document: deleteDocument,
            variables: { strategyId: strategy.id },
          })
          const ok = result.deleteCustomerStrategy?.result ?? false
          if (!ok) {
            throw new Error(t('update_failed_5'))
          }
        }),
      )

      for (const [index, strategy] of nextStrategies.entries()) {
        if (strategy.id == '') {
          const result = await graphQLRequest({
            document: createDocument,
            variables: { strategyMemoId },
          })

          const strategyId = result.createCustomerStrategy?.id
          if (!exists(strategyId)) {
            throw new Error(t('update_failed_6'))
          }
          setValue(`strategies.${index}.id`, strategyId)
          strategy.id = strategyId
        }

        const result = await graphQLRequest({
          document: updateDocument,
          variables: { strategyId: strategy.id, ...R.omit(strategy, ['id']) },
        })
        const ok = exists(result.updateCustomerStrategy?.id)
        if (!ok) {
          throw new Error(t('update_failed_7'))
        }
      }
    },
    ...options,
  })
}
